import React, { useCallback } from 'react'

import { Modal } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import CancelStep from './CancelStep'
import ConsequencesList from './ConsequencesList'
import PauseOffer from './PauseOffer'
import ReasonSelect from './ReasonSelect'
import ReasonStep from './ReasonStep'
import useCancelModal, { CANCEL_MODAL_STEP } from './useCancelModal'

function CancelSubscriptionModal({
  isOpen,
  onClose,
  onSubmit,
  onReasonCapture = null,
  subscriptionId,
  tier = null,
  feeData = null,
  feeError = null,
  isFeeLoading = false,
  locale = 'en',
  subscriptionV16Enabled = false,
  planType = 'yearly',
  gracePeriodEligible = true,
  showPauseOffer = false,
  onPause = null,
  isPauseLoading = false,
}) {
  const {
    step,
    handleContinueToCancel,
    handlePauseClick,
    handleContinueFromReason,
    handleBackFromReason,
    handleBackToReason,
    isContinueFromReasonDisabled,
    hasReasonStep,
    feeBreakdown,
    hasFee,
    showInlineReasonSelect,
    accessUntilFormatted,
    isAcknowledged,
    setIsAcknowledged,
    selectedReason,
    isOtherSelected,
    otherText,
    handleReasonChange,
    handleOtherTextChange,
    handleSubmit,
    handleClose,
    isSubmitDisabled,
    isSubmitting,
    maxCustomReasonLength,
  } = useCancelModal({
    isOpen,
    onClose,
    onSubmit,
    onReasonCapture,
    subscriptionId,
    tier,
    feeData,
    feeError,
    isFeeLoading,
    locale,
    subscriptionV16Enabled,
    planType,
    showPauseOffer,
    onPause,
    isPauseLoading,
  })

  const translate = useCallback(
    (key) => I18n.t(
      `subscription_management.cancel_modal.${key}`,
      { ns: 'rails', lng: locale }
    ),
    [locale]
  )

  const showAccessUntilNotice = Boolean(!hasFee && !feeError && accessUntilFormatted)

  const reasonSelect = (
    <ReasonSelect
      selectedReason={selectedReason}
      isOtherSelected={isOtherSelected}
      otherText={otherText}
      onReasonChange={handleReasonChange}
      onOtherTextChange={handleOtherTextChange}
      maxCustomReasonLength={maxCustomReasonLength}
      locale={locale}
    />
  )

  if (step === CANCEL_MODAL_STEP.REASON) {
    return (
      <ReasonStep
        isOpen={isOpen}
        onClose={handleClose}
        onBack={handleBackFromReason}
        onContinue={handleContinueFromReason}
        isContinueDisabled={isContinueFromReasonDisabled}
        showBackButton={showPauseOffer}
        showAccessUntilNotice={showAccessUntilNotice}
        accessUntilFormatted={accessUntilFormatted}
        reasonSelect={reasonSelect}
        locale={locale}
      />
    )
  }

  if (step === CANCEL_MODAL_STEP.PAUSE_OFFER) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        header={translate('title')}
        width="small"
        canClose
        // Triangle Modal closes its internal state before calling onClose, so a close attempt
        // during an in-flight pause must be blocked here — a no-op onClose would leave the
        // modal hidden while the parent still considers it open.
        disableClose={isPauseLoading}
        customClass="cancel-subscription-modal cancel-subscription-modal--pause-offer"
      >
        <PauseOffer
          onPause={handlePauseClick}
          onContinueToCancel={handleContinueToCancel}
          subscriptionId={subscriptionId}
          tier={tier}
          isPauseLoading={isPauseLoading}
          locale={locale}
        />
      </Modal>
    )
  }

  return (
    <CancelStep
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={handleSubmit}
      onBack={handleBackToReason}
      hasReasonStep={hasReasonStep}
      hasFee={hasFee}
      hasFeeError={Boolean(feeError)}
      feeBreakdown={feeBreakdown}
      accessUntilFormatted={accessUntilFormatted}
      showAccessUntilNotice={showAccessUntilNotice}
      isAcknowledged={isAcknowledged}
      onAcknowledgeChange={setIsAcknowledged}
      isSubmitDisabled={isSubmitDisabled}
      isSubmitting={isSubmitting}
      consequencesList={hasReasonStep ? (
        <ConsequencesList
          hasFee={hasFee}
          planType={planType}
          gracePeriodEligible={gracePeriodEligible}
          locale={locale}
        />
      ) : null}
      reasonSelect={showInlineReasonSelect ? reasonSelect : null}
      locale={locale}
    />
  )
}

CancelSubscriptionModal.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onSubmit: func.isRequired,
  onReasonCapture: func,
  subscriptionId: number.isRequired,
  tier: string,
  feeData: shape({
    cancel_fee: string,
    access_until: string,
    remaining_months: number,
    monthly_rate: string,
    total_remaining: string,
  }),
  feeError: shape({
    message: string,
  }),
  isFeeLoading: bool,
  locale: string,
  subscriptionV16Enabled: bool,
  planType: string,
  gracePeriodEligible: bool,
  showPauseOffer: bool,
  onPause: func,
  isPauseLoading: bool,
}

export default CancelSubscriptionModal
